// ============================================================
// Property – Response & Domain Types
// ============================================================

import {
  LandTitleDocument,
  LeaseDurationUnit,
  ListingType,
  PropertyApprovalStatus,
  PropertyAvailabilityStatus,
  PropertyCategory,
  PropertyCurrencies,
  PropertyStage,
  PropertyType,
  Topography,
} from "./property-config";

export type RealtorVerificationStatus = "verified" | "unverified" | "pending";

/**
 * Normalizes the backend `verified` field (boolean legacy, tri-state
 * current) to the tri-state UI status. Unknown/missing values fail closed
 * to "unverified" so no badge renders. Source of truth for realtor
 * verification mapping — import this instead of redefining it.
 */
export const mapApiVerifiedStatus = (
  value?: boolean | string | null,
): RealtorVerificationStatus => {
  if (value === true || value === "verified") return "verified";
  if (value === "pending") return "pending";
  return "unverified";
};

export interface Realtor {
  id: string;
  first_name: string;
  last_name: string;
  business_name?: string;
  slug?: string;
  plan?: string;
  is_partner?: boolean;
  verified?: RealtorVerificationStatus;
  partner_since: string;
  partnerStatus: "pending" | "approved" | "requested";
  email: string;
  phone?: string;
  join_date: string;
}

export interface CreatedBy {
  id: string;
  slug?: string;
  full_name: string;
  email: string;
  phone_number: string;
  role: string;
  profile_image_url: string | null;
  state: string | null;
  city: string | null;
}

export interface Property {
  id: string;
  slug?: string;
  reference: string;
  address: string;
  description: string;
  status: string; // LEGACY/DEPRECATED — still returned; use approval_status/availability_status
  approval_status?: PropertyApprovalStatus;
  availability_status?: PropertyAvailabilityStatus;
  listing_type: string;
  property_type: string;
  currency?: PropertyCurrencies;
  price: number;
  is_negotiable?: boolean;
  agency_fees?: number;
  legal_fees?: number;
  caution_fee?: number;
  service_fee?: number;
  toilet?: number;
  total_package?: number;
  bedroom_count?: number;
  bathroom_count?: number;
  number_of_days?: number;
  number_of_plots?: number;
  serviced?: boolean;
  shared?: boolean;
  furnished?: boolean;
  property_stage?: string;
  additional_info?: string;
  amenities?: string[];
  image_url: string[];
  /** Explicit, admin-settable social share image (one per property). */
  og_image_url?: string | null;
  /** FFmpeg-extracted poster for the primary video, when one exists. */
  video_poster_url?: string | null;
  realtor: Realtor;
  promote_to_weekly_deals: boolean;
  request_inspection: boolean;
  city: string;
  state: string;
  state_slug?: string | null;
  city_slug?: string | null;
  property_category?: string;
  lease_duration?: number;
  lease_duration_unit?: string;
  total_floor_area_sqm?: number;
  available_floor_space?: number;
  number_of_floors?: number;
  parking_capacity_cars?: number;
  is_multi_tenanted?: boolean;
  clear_ceiling_height_meters?: number;
  loading_docks_count?: number;
  power_capacity_kva?: number;
  total_land_area_sqm?: number;
  covered_area_sqm?: number;
  floor_load_capacity_tons?: number;
  topography?: string;
  land_title_document?: string;
  created_at: string;
  is_saved?: boolean;
  impressions_count: number;
  page_opened_count: number;
  inspection_count: number;
  save_count: number;
  whatsapp_contact_count: number;
  createdBy: CreatedBy | null;
  longitude?: number;
  latitude?: number;
  sale_document_details?: string;
}

export interface PaginatedResponse<T> {
  status: string;
  message: string;
  data: T[];
  metadata: {
    cursor: string | null;
    beforeCursor: string | null;
    pageNumber: number;
    page?: number;
    totalCount?: number;
    pageSize?: number;
  };
}

export interface GetPropertyByUserResponse {
  status: string;
  message: string;
  data: Property[];
  metadata: {
    totalCount: number;
    pageSize: number;
    currentPage: string;
  };
  statCount: {
    total: number;
    sale: number;
    rent: number;
    shortlet: number;
    lease: number;
    land: number;
  };
}

// ---- Endpoint-specific Response Data Shapes ----

export interface AddPropertyData {
  propertyId: string;
}

export interface HasRequestedData {
  hasRequested: boolean;
}

export interface EditOwnPropertyData {
  property: Property;
  statusReset?: boolean;
}

export interface PropertyMetrics {
  totalCount: number;
  totalCountByListingType: Record<string, number>;
  totalCountByStatus: Record<string, number>;
  totalCountByPropertyType: Record<string, number>;
  totalCountByTimePeriod: Record<string, number>;
}

// ---- Request Body Types ----

export interface AddPropertyPayload {
  // General 1 --
  propertyCategory: PropertyCategory; // required
  listingType: ListingType; // required
  propertyType?: PropertyType; // required
  propertyStage?: PropertyStage; // required
  address: string; // required
  state: string; // optional - do not validate this, address is powered by google, once an address is inputed, state and city are injected
  city: string; // optional - do not validate
  longitude?: number; // optional - auto injected
  latitude?: number; // optional - auto injected
  description: string; // required
  additionalInfo?: string; // optional
  amenities?: string[]; // optional
  imageUrl: string[]; // required at least 1
  promoteWeekly: boolean; // either true or false , no need for validation
  requestInspection: boolean; // either true or false no need for validation

  // Pricing 2 --
  currency?: PropertyCurrencies; // required
  price: number; // required
  is_negotiable?: boolean; // either true or false, no need for validation
  agencyFees?: number; // optional
  serviceFee?: number; // optional
  legalFees?: number; // optional
  cautionFee?: number; // optional
  totalPackage?: number; // optional - auto calculated

  // category based fields
  // EXISTING
  // category: [Residential]
  // listingType: [All]
  bedroomCount?: number; // required for residential category - validate
  bathroomCount?: number; // required for residential category - validate
  toilet?: number; // optional

  // category: [Residential, Commercial]
  // listingType: [All]
  furnished?: boolean; // required - for residentieal or commercial - validate
  serviced?: boolean; // required - for residential or commercial - validate
  shared?: boolean; // required - for residential or commercial - validate

  // category: [Commercial]
  // listingType: [All]

  // category: [Residential, Commercial, Industrial, Special Purpose, Land]
  // listingType: [Shortlet]
  numberOfDays?: number; // required for shortlet- validate for shortlet
  // listingType: [Lease]
  lease_duration?: number; // required for lease - validate for lease
  lease_duration_unit?: LeaseDurationUnit; // required for lease - validate for lease

  // category: [Land (SQM) Label this SQM]
  // listingType: [All]
  numberOfPlots?: number; // validate this only for land

  // NEW OPTIONALS
  // category: [Commercial]
  // listingType: [All]

  totalFloorAreaSqm?: number; // optional
  availableFloorSpace?: number; // optional
  numberOfFloors?: number; // optional
  parkingCapacityCars?: number; // optional
  isMultiTenanted?: boolean; // optional

  // category: [Industrial]
  // listingType: [All]
  clearCeilingHeightMeters?: number; // optional
  loadingDocksCount?: number; // optional
  powerCapacityKVA?: number; // optional
  totalLandAreaSqm?: number; // optional
  coveredAreaSqm?: number; // optional
  floorLoadCapacityTons?: number; // optional

  // category: [Land]
  // listingType: [All]
  topography?: Topography; // optional

  // listingType: [Sale]
  landTitleDocument?: LandTitleDocument; // validate for land category and sale listing type
  // listingType: [Sale]
  saleDocumentDetails?: string; // validate for non land and sale listing type.
}

export interface EditPropertyPayload {
  id: string;
  address?: string | null;
  description?: string | null;
  price?: number | null;
  currency?: PropertyCurrencies | null;
  is_negotiable?: boolean | null;
  agencyFees?: number | null;
  legalFees?: number | null;
  cautionFee?: number | null;
  serviceFee?: number | null;
  toilet?: number | null;
  totalPackage?: number | null;
  bedroomCount?: number | null;
  bathroomCount?: number | null;
  numberOfDays?: number | null;
  numberOfPlots?: number | null;
  propertyType?: PropertyType | null;
  listingType?: ListingType | null;
  propertyCategory?: PropertyCategory | null;
  propertyStage?: PropertyStage | null;
  furnished?: boolean | null;
  shared?: boolean | null;
  serviced?: boolean | null;
  amenities?: string[] | null;
  image_url?: string[] | null;
  longitude?: number | null;
  latitude?: number | null;
  city?: string | null;
  state?: string | null;
  additionalInfo?: string | null;
  saleDocumentDetails?: string | null;
  lease_duration?: number | null;
  lease_duration_unit?: LeaseDurationUnit | null;
  totalFloorAreaSqm?: number | null;
  availableFloorSpace?: number | null;
  numberOfFloors?: number | null;
  parkingCapacityCars?: number | null;
  isMultiTenanted?: boolean | null;
  clearCeilingHeightMeters?: number | null;
  loadingDocksCount?: number | null;
  powerCapacityKVA?: number | null;
  totalLandAreaSqm?: number | null;
  coveredAreaSqm?: number | null;
  floorLoadCapacityTons?: number | null;
  topography?: Topography | null;
  landTitleDocument?: LandTitleDocument | null;
  acknowledgeReapproval?: boolean | null;
  promoteWeekly?: boolean | null;
  requestInspection?: boolean | null;
}

export interface LiveInspectionPayload {
  propertyId: string;
  paymentSource: string;
  transactionId?: string;
}

export interface SavePropertyPayload {
  propertyId: string;
}

export interface PropertyQueryParams {
  pageSize?: number;
  pageNumber?: number;
  cursor?: string;
  beforeCursor?: string;
  location?: string;
  propertyType?: string;
  status?: string; // LEGACY/DEPRECATED — prefer approvalStatus / availabilityStatus
  approvalStatus?: PropertyApprovalStatus;
  availabilityStatus?: PropertyAvailabilityStatus;
  maxPrice?: string;
  minPrice?: string;
  bedroomCount?: string;
  bathroomCount?: string;
  listingType?: string;
  isFurnished?: boolean;
  isServiced?: boolean;
  isShared?: boolean;
  city?: string;
  state?: string;
  citySlug?: string;
  stateSlug?: string;
  propertyCategory?: string;
  searchQuery?: string;
  latitude?: string;
  longitude?: string;
}

export interface HomepageQuery {
  location?: string;
  latitude?: string;
  longitude?: string;
  page?: number;
  pageSize?: number;
}

export interface GetPropertiesByRealtorParams {
  realtorId?: string;
  pageSize?: number;
  pageNumber?: number;
  cursor?: string;
  beforeCursor?: string;
  location?: string;
  propertyType?: string;
  status?: string;
}

export interface GetSavedPropertiesResponse {
  status: string;
  message: string;
  data: Property[];
  metadata: {
    cursor: string | null;
    beforeCursor: string | null;
    pageNumber: number;
    page: number;
  };
}

export interface PlatformLocationsQuery {
  state?: string;
}

export interface PlatformLocationCity {
  name: string;
  slug: string;
  listing_count: number;
}

export interface PlatformLocationState {
  name: string;
  slug: string;
  code: string;
  listing_count: number;
  cities: PlatformLocationCity[];
}

export interface PlatformLocationTotals {
  states: number;
  states_with_listings: number;
  cities: number;
  listings: number;
}

export interface PlatformLocationsData {
  schema_version: number;
  generated_at: string;
  stale: boolean;
  totals: PlatformLocationTotals;
  states: PlatformLocationState[];
}

export interface SeoFacetRow {
  listing_type: string;
  property_category: string | null;
  property_type: string | null;
  state: string;
  state_slug: string;
  city: string | null;
  city_slug: string | null;
  count: number;
  last_updated_at: string;
}

export interface SeoIndexData {
  schema_version: number;
  generated_at: string;
  stale: boolean;
  rows: SeoFacetRow[];
}

export interface PropertySitemapEntry {
  id: string;
  loc: string;
  slug: string | null;
  listing_type: string | null;
  property_category: string | null;
  property_type: string | null;
  state: string | null;
  state_slug: string | null;
  city: string | null;
  city_slug: string | null;
  lastmod: string;
}
